import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = ["in_bin", "in_transit", "in_transit_to_regional", "at_regional", "in_transit_to_site", "at_site", "installed", "active", "return_in_transit", "returned", "written_off"];

export default function Ledger() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [ip1Product, setIp1Product] = useState("");
  const [ip1Serials, setIp1Serials] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const canIP1 = ["warehouse_manager", "operations_controller", "system_admin"].includes(user.role);
  const canReset = user.role === "system_admin";

  const load = useCallback(() => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/units${q}`).then((r) => setUnits(r.data));
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const triggerIP1 = async () => {
    const serials = ip1Serials.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!ip1Product || serials.length === 0) { toast.error("Pick a product and enter serials"); return; }
    try {
      const { data } = await api.post("/inbox/ip1", { product_id: ip1Product, serials });
      toast.success(`IP1 received — ${data.created.length} unit(s) added to the bin`);
      setOpen(false); setIp1Serials("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "IP1 failed");
    }
  };

  const runTrainingReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/training-reset", { confirm: "RESET-TRAINING" });
      toast.success(`Fresh batch loaded — ${data.seeded} ${data.product} in the bin (${data.serials_range[0]}…${data.serials_range[1]})`);
      setResetOpen(false);
      setFilter("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Training reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Stock Ledger" subtitle="Every serialised consignment unit and its current location.">
        <Select testid="ledger-status-filter" value={filter} onChange={setFilter} placeholder="All statuses"
          options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
        {canIP1 && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-ip1" className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Trigger IP1 (add stock)</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Simulate IP1 — Stock Issued to Bin</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="overline text-slate-500">Product</Label>
                  <div className="mt-1"><Select testid="ip1-product" value={ip1Product} onChange={setIp1Product}
                    options={products.map((p) => ({ value: p.id, label: `${p.name} (VBU ${p.vbu_codes})` }))} placeholder="Select product" /></div>
                </div>
                <div>
                  <Label className="overline text-slate-500">Serial numbers (comma or newline separated)</Label>
                  <Textarea data-testid="ip1-serials" value={ip1Serials} onChange={(e) => setIp1Serials(e.target.value)} rows={4}
                    placeholder={"35678901234567\n35678901234568"} className="rounded-sm mt-1 font-label text-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="submit-ip1" onClick={triggerIP1} className="rounded-sm bg-[#0B2545] font-head">Post to Bin</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {canReset && (
          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-training-reset" variant="outline" className="rounded-sm border-[#b45309] text-[#b45309] hover:bg-[#b45309] hover:text-white font-head">Load fresh training batch</Button>
            </DialogTrigger>
            <DialogContent data-testid="training-reset-dialog">
              <DialogHeader><DialogTitle>Load a fresh training batch?</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm text-slate-600">
                <p>This resets the platform to a clean starting point for a training run. It will:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-medium text-[#b45309]">Delete</span> all units/serials and every transactional record (distributions, replenishment requests, fitments, returns, AX inbox messages, notices).</li>
                  <li>Reset the central-bin direct-fulfilment toggle to off.</li>
                  <li><span className="font-medium text-[#00857C]">Keep</span> all users, products, locations and stock rules.</li>
                  <li><span className="font-medium text-[#00857C]">Seed 100 × STARTag</span> (STG-0001…STG-0100) into the Midrand bin as a fresh AX P1 posting.</li>
                </ul>
                <p className="text-[#b45309] font-medium">This cannot be undone. Use it only to (re)start the training exercise.</p>
              </div>
              <DialogFooter>
                <Button data-testid="cancel-training-reset" variant="outline" onClick={() => setResetOpen(false)} className="rounded-sm font-head">Cancel</Button>
                <Button data-testid="confirm-training-reset" onClick={runTrainingReset} disabled={resetting} className="rounded-sm bg-[#b45309] hover:bg-[#b45309]/90 font-head">{resetting ? "Loading…" : "Yes, load fresh batch"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <Panel>
        {units.length === 0 ? <Empty>No units. Trigger IP1 to load consignment stock into the main bin.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="units-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Product</th><th className="py-2 pr-3">VBU</th>
                  <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Location</th><th className="py-2 pr-3">WO / Channel</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-label text-[13px]">{u.serial}</td>
                    <td className="py-2 pr-3 font-medium text-[#0B2545]">{u.product_name}</td>
                    <td className="py-2 pr-3 text-slate-500">{u.vbu_code}</td>
                    <td className="py-2 pr-3"><StatusBadge status={u.status} /></td>
                    <td className="py-2 pr-3 text-slate-600">{u.location_name}</td>
                    <td className="py-2 pr-3 text-slate-500 text-xs">{u.work_order_no ? `${u.work_order_no} · ${u.channel}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
